import React, { useState } from 'react';
import { Building2, Users, Briefcase, TrendingUp } from 'lucide-react';

import frontal from '../assets/images/estructura/IMG_2518.jpg';
import lobby from '../assets/images/estructura/IMG_2507.jpg';
import tren from '../assets/images/estructura/IMG_2494.jpg';
import camiones from '../assets/images/estructura/IMG_2484.jpg';
import vista11 from '../assets/images/estructura/IMG_2477.jpg';

const FacilitiesSection = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  const facilities = [
    {
      id: 1,
      title: "Oficinas Corporativas",
      subtitle: "Espacios de trabajo",
      description: "Diseñadas para maximizar la productividad con tecnología de vanguardia y ambientes inspiradores.",
      image: frontal,
      icon: Building2,
      color: "from-slate-600 to-slate-800"
    },
    {
      id: 2,
      title: "Lobby", 
      subtitle: "Espacios de recepción",
      description: "Diseñados para causar una excelente primera impresión con un diseño moderno y acogedor.",
      image: lobby,
      icon: TrendingUp,
      color: "from-blue-600 to-blue-800"
    },
    {
      id: 3,
      title: "Áreas Colaborativas",
      subtitle: "Espacios de encuentro",
      description: "Zonas diseñadas para fomentar la creatividad y el trabajo en equipo en un ambiente relajado.",
      image: tren,
      icon: Users,
      color: "from-indigo-600 to-indigo-800"
    },
    {
      id: 4,
      title: "Centro de Bienestar",
      subtitle: "Espacios para el equilibrio",
      description: "Instalaciones dedicadas al bienestar físico y mental de nuestro equipo de trabajo.",
      image: camiones,
      icon: Briefcase,
      color: "from-slate-700 to-slate-900"
    }
    ,
    {
      id: 5,
      title: "Producción",
      subtitle: "Área de producción y empaque",
      description: "Zona dedicada al armado de costales, empaque y logística de productos terminados. Aquí se optimizan los procesos para garantizar calidad y eficiencia en cada entrega.",
      image: vista11,
      icon: Briefcase,
      color: "from-blue-600 to-blue-800"
    }
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(15 23 42) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header Section */}
        <div className="max-w-3xl mb-20">
          
          <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Nuestras
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Instalaciones
            </span>
          </h2>
          
          <p className="text-xl text-slate-600 leading-relaxed">
            Espacios estratégicamente diseñados para impulsar el crecimiento, la innovación y el bienestar de nuestro equipo.
          </p>
        </div>

        {/* Facilities Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {facilities.map((facility) => {
            const Icon = facility.icon;
            const isActive = activeCard === facility.id;
            
            return (
              <div
                key={facility.id}
                onMouseEnter={() => setActiveCard(facility.id)}
                onMouseLeave={() => setActiveCard(null)}
                onClick={() => setModalImage(facility.image)}
                className={`group relative bg-white rounded-2xl overflow-hidden transition-transform duration-300 cursor-pointer ${
                  isActive ? 'shadow-2xl shadow-slate-900/10 scale-[1.02]' : 'shadow-lg shadow-slate-900/5'
                }`}
                style={{ willChange: 'transform' }}
              >
                {/* Top Accent Line */}
                <div className={`h-1 bg-gradient-to-r ${facility.color} transform origin-left transition-transform duration-300 ${
                  isActive ? 'scale-x-100' : 'scale-x-0'
                }`}></div>

                {/* Image Section */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={facility.image}
                    alt={facility.title}
                    loading="lazy"
                    width={160}
                    height={160}
                    className="w-full h-full object-cover"
                    style={
                      facility.title === 'Oficinas Corporativas' ? { objectPosition: 'center 20%' } :
                      facility.title === 'Lobby' ? { objectPosition: 'center top' } :
                      facility.title === 'Producción' ? { objectPosition: 'center top' } :
                      {}
                    }
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 left-4">
                    <div className={`p-3 rounded-xl bg-white/95 backdrop-blur-sm transition-transform duration-300 ${
                      isActive ? 'scale-110 rotate-3' : 'scale-100'
                    }`} style={{ willChange: 'transform' }}>
                      <Icon className="w-6 h-6 text-slate-900" />
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  {/* Content */}
                  <div>
                    <div className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">
                      {facility.subtitle}
                    </div>
                    
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      {facility.title}
                    </h3>
                    
                    <p className="text-slate-600 leading-relaxed mb-6">
                      {facility.description}
                    </p>

                  </div>
                </div>

                {/* Bottom Gradient */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${facility.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              </div>
            );
          })}
        </div>

        {/* Modal for Image Preview */}
        {modalImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-900/90 via-slate-800/80 to-slate-900/90 backdrop-blur-md transition-opacity duration-300 animate-fadeIn" onClick={() => setModalImage(null)}>
            <div className="relative max-w-2xl w-full mx-4 flex flex-col items-center" onClick={e => e.stopPropagation()}>
              <img src={modalImage} alt="Instalación" className="w-full max-h-[70vh] object-contain rounded-3xl shadow-2xl border-4 border-white transition-transform duration-300 animate-zoomIn" />
              <button
                className="absolute top-2 right-2 bg-white text-slate-900 rounded-full p-2 shadow-lg hover:bg-blue-100 transition-colors duration-200"
                onClick={() => setModalImage(null)}
                aria-label="Cerrar"
              >
                <span className="text-2xl font-bold">×</span>
              </button>
              {/* Información de la instalación */}
              {facilities.find(f => f.image === modalImage) && (
                <div className="mt-6 text-center px-4">
                  <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">{facilities.find(f => f.image === modalImage).title}</h3>
                  <p className="text-lg text-slate-200 mb-2">{facilities.find(f => f.image === modalImage).subtitle}</p>
                  <p className="text-base text-slate-300">{facilities.find(f => f.image === modalImage).description}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FacilitiesSection;